/**
 * tokens/injector.js - Inject scoped CSS variables into document
 * Location: /app/frontend/src/admin/page_builder/tokens/
 * 
 * Manages a <style> element in document.head for token CSS variables.
 * Updates the style content when tokens change.
 */

import { generateScopedCSS, TOKEN_SCOPE_CLASS } from './cssGenerator';

/**
 * ID for the injected style element
 */
const STYLE_ELEMENT_ID = 'tc-token-styles';

/**
 * Cache for last injected CSS to avoid redundant DOM updates
 */
let lastInjectedCSS = '';

/**
 * Get or create the style element for token injection
 * @returns {HTMLStyleElement}
 */
function getStyleElement() {
  let styleEl = document.getElementById(STYLE_ELEMENT_ID);
  
  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = STYLE_ELEMENT_ID;
    styleEl.setAttribute('data-tc-tokens', 'true');
    document.head.appendChild(styleEl);
  }
  
  return styleEl;
}

/**
 * Inject token CSS variables into document head
 * Creates or updates a <style> element with scoped CSS variables.
 * 
 * @param {object} tokens - Token object to inject
 * @returns {void}
 */
export function injectTokenCSS(tokens) {
  const css = generateScopedCSS(tokens);
  
  // Skip if CSS hasn't changed
  if (css === lastInjectedCSS) {
    return;
  }
  
  const styleEl = getStyleElement();
  styleEl.textContent = css;
  lastInjectedCSS = css;
}

/**
 * Remove the injected token styles from document
 * @returns {void}
 */
export function removeTokenCSS() {
  const styleEl = document.getElementById(STYLE_ELEMENT_ID);
  if (styleEl) {
    styleEl.remove();
    lastInjectedCSS = '';
  }
}

/**
 * Check if token CSS is currently injected
 * @returns {boolean}
 */
export function isTokenCSSInjected() {
  return !!document.getElementById(STYLE_ELEMENT_ID);
}

/**
 * Get the current injected CSS (for debugging)
 * @returns {string}
 */
export function getInjectedCSS() {
  const styleEl = document.getElementById(STYLE_ELEMENT_ID);
  return styleEl ? styleEl.textContent : '';
}

/**
 * Apply the token scope class to an element
 * @param {HTMLElement} element - Element to scope
 * @returns {void}
 */
export function applyTokenScope(element) {
  if (element && !element.classList.contains(TOKEN_SCOPE_CLASS)) {
    element.classList.add(TOKEN_SCOPE_CLASS);
  }
}

/**
 * Remove the token scope class from an element
 * @param {HTMLElement} element - Element to unscope
 * @returns {void}
 */
export function removeTokenScope(element) {
  if (element) {
    element.classList.remove(TOKEN_SCOPE_CLASS);
  }
}

export { TOKEN_SCOPE_CLASS };

export default {
  injectTokenCSS,
  removeTokenCSS,
  isTokenCSSInjected,
  getInjectedCSS,
  applyTokenScope,
  removeTokenScope,
  TOKEN_SCOPE_CLASS
};
